import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './uploadRequiredDocuments.scss';
import { InputTextarea } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { SLICES } from '../../constants/slices';
import { PetSelection } from '../../components/claims/newClaim/newClaimSteps/petSelection';
import { useGetPetInfoQuery } from '../../data/policy';
import { sanitizedAll } from '../../utils/sanitize';
import { UPLOAD_DOCUMENTS_FORM_KEYS } from '../../constants/formKeys';
import { mapUploadFileChecklist, mapUploadFileInfo, sortedMissingDocsStatuses } from './mapMissingDocStatus';
import { CSPUrlsMap } from '../../routes/CSPUrlsMap';
import {
  useGetUploadAdditionalDocumentsDraftQuery,
  useSaveUploadAdditionalDocumentsMutation,
  useSubmitAdditionalClaimDocumentsMutation,
  useUploadSelectedClaimDocumentsMutation
} from '../../data/claim';
import ClaimSavedSuccess from '../../components/claims/claimSavedSuccess/claimSavedSuccess';
import Loading from '../../components/shared/Loading/Loading';
import { getCommonPayload } from './getPayload';
import ClaimSubmitSuccess from '../../components/claims/claimSubmitSuccess/claimSubmitSuccess';

const UploadRequiredDocuments = () => {
  const navigate = useNavigate();
  const formContext = useFormContext();
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;
  const [showSavedSuccess, setShowSavedSuccess] = useState(false);
  const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);

  const location = useLocation();
  const { claim } = location.state || {};
  const { FullClaimNumber, PetName, ClaimDescription, PolicyNumber, ReceivedDate, ClaimDetailsDocuments, ClaimNumber } =
    claim || {};

  const [additionalAttachementsByType, setAdditionalAttachmentsByType] = useState({});
  const [uploadingKeys, setUploadingKeys] = useState({});
  const isAnyUploading = Object.values(uploadingKeys).some(Boolean);

  const handleUploadingChange = useCallback((formKey, isUploading) => {
    setUploadingKeys((prev) => ({ ...prev, [formKey]: isUploading }));
  }, []);
  const { data: petDetails, isLoading: petDetailsLoading } = useGetPetInfoQuery(auth0AccessToken, {
    skip: !auth0AccessToken
  });
  const { data: additionalDocumentsDraft, isLoading: uploadAdditionalDocumentsDraftLoading } =
    useGetUploadAdditionalDocumentsDraftQuery(ClaimNumber);
  const [saveUploadAdditionalDocuments, { isLoading: saveUploadAdditionalDocumentsLoading }] =
    useSaveUploadAdditionalDocumentsMutation({});
  const [submitAdditionalClaimDocumentsMutation, { isLoading: submitAdditionalClaimDocumentsLoading }] =
    useSubmitAdditionalClaimDocumentsMutation({});
  const [uploadSelectedClaimDocuments, { isLoading: isUploading }] = useUploadSelectedClaimDocumentsMutation();

  const uploadedDateBy = ReceivedDate
    ? new Date(new Date(ReceivedDate).setDate(new Date(ReceivedDate).getDate() + 34))
    : null;

  const missingDocs = ClaimDetailsDocuments?.UploadClaimMissingDocs?.MissingDocumentTypeTreatmentDates || [];
  const missingDocsStatuses = sortedMissingDocsStatuses(Object.keys(missingDocs));

  const uploadPendingFiles = async (formData) => {
    const documentTypes = Object.values(UPLOAD_DOCUMENTS_FORM_KEYS).filter(
      (key) => !key.endsWith('_CHECKLIST') && key !== UPLOAD_DOCUMENTS_FORM_KEYS.COMMENTS
    );
    const uploads = await documentTypes.reduce(async (uploadsPromise, documentType) => {
      const uploadedDocuments = await uploadsPromise;
      const files = Array.from(formData[documentType] || []);
      const pendingFiles = files.filter((file) => !file.path);

      if (pendingFiles.length === 0) {
        return [...uploadedDocuments, { documentType, files }];
      }

      const uploadFormData = new FormData();
      pendingFiles.forEach((file, index) => uploadFormData.append(index.toString(), file));
      uploadFormData.append('claimNumber', ClaimNumber);
      uploadFormData.append('docType', documentType);

      const response = await uploadSelectedClaimDocuments(uploadFormData).unwrap();
      const uploadedFiles = response.ClaimFiles || [];
      if (uploadedFiles.length !== pendingFiles.length) {
        throw new Error('Error uploading documents: uploaded file count mismatch');
      }

      return [
        ...uploadedDocuments,
        {
          documentType,
          files: files.map((file) => {
            if (file.path) {
              return file;
            }

            const uploadedFile = uploadedFiles[pendingFiles.indexOf(file)];
            return Object.assign(file, {
              documentId: uploadedFile?.DocumentId,
              path: uploadedFile?.DocumentPath,
              documentType: uploadedFile?.DocumentType,
              claimFileId: uploadedFile?.ClaimFileId
            });
          })
        }
      ];
    }, Promise.resolve([]));

    return uploads.reduce(
      (uploadedFormData, { documentType, files }) => ({ ...uploadedFormData, [documentType]: files }),
      formData
    );
  };

  const onFormSubmit = async () => {
    formContext.clearErrors();
    const formData = formContext.watch();
    const missingUploadDocKeys = Object.keys(missingDocs);
    const missingUploadDocKeysChecklists = missingUploadDocKeys.map((key) => `${key}_CHECKLIST`);
    const isValid = await formContext.trigger([
      UPLOAD_DOCUMENTS_FORM_KEYS.COMMENTS,
      ...missingUploadDocKeys,
      ...missingUploadDocKeysChecklists
    ]);

    if (isValid) {
      try {
        const uploadedFormData = await uploadPendingFiles(formData);
        const payload = getCommonPayload(uploadedFormData, ClaimNumber, PolicyNumber);
        await submitAdditionalClaimDocumentsMutation(payload).unwrap();
        setShowSubmitSuccess(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error submitting documents', error);
      }
    }
  };

  const handleSaveDraft = async () => {
    const formData = formContext.getValues();

    try {
      const uploadedFormData = await uploadPendingFiles(formData);
      const payload = getCommonPayload(uploadedFormData, ClaimNumber, PolicyNumber);
      await saveUploadAdditionalDocuments(payload).unwrap();
      setShowSavedSuccess(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving draft', error);
    }
  };

  // Prefill form with additionalDocumentsDraft when loaded
  useEffect(() => {
    if (additionalDocumentsDraft) {
      // Set checklist values
      if (Array.isArray(additionalDocumentsDraft.DocumentTypeCheckLists)) {
        additionalDocumentsDraft.DocumentTypeCheckLists.forEach((item) => {
          if (item.DocumentType && typeof item.IsCheck === 'boolean') {
            formContext.setValue(`${item.DocumentType}_CHECKLIST`, item.IsCheck);
          }
        });
      }

      // Set comments
      if (additionalDocumentsDraft.ClaimComment && additionalDocumentsDraft.ClaimComment.Comment) {
        formContext.setValue(UPLOAD_DOCUMENTS_FORM_KEYS.COMMENTS, additionalDocumentsDraft.ClaimComment.Comment);
      }

      // Set attachments
      if (Array.isArray(additionalDocumentsDraft.ClaimAttachments)) {
        // Group attachments by DocumentType
        const attachmentsByType = additionalDocumentsDraft.ClaimAttachments.reduce((acc, att) => {
          if (!acc[att.DocumentType]) acc[att.DocumentType] = [];
          acc[att.DocumentType].push(att);
          return acc;
        }, {});
        setAdditionalAttachmentsByType(attachmentsByType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalDocumentsDraft]);

  if (
    saveUploadAdditionalDocumentsLoading ||
    uploadAdditionalDocumentsDraftLoading ||
    submitAdditionalClaimDocumentsLoading
  ) {
    return <Loading />;
  }
  if (showSavedSuccess) {
    return (
      <ClaimSavedSuccess copy="Please ensure you finalise and submit the required information so we can progress your claim." />
    );
  }
  if (showSubmitSuccess) {
    return (
      <ClaimSubmitSuccess
        title="Submitted"
        subTitle="Thanks, we&rsquo;ve received your documents."
        copy={<p>You can continue to track the status of your claim on the dashboard.</p>}
      />
    );
  }

  const handleBackToDashboard = () => {
    formContext.clearErrors();
    navigate(CSPUrlsMap.dashboard.url);
  };

  return (
    <div>
      <h1 className="mainPageHeader">Complete My Claim</h1>
      <h2>Upload required documents for claim {FullClaimNumber}</h2>

      <div className="titleSection">
        <p>We need further information to complete your claim. Please upload the required documentation below.</p>
        <p>
          Please note: If we do not receive this information by {uploadedDateBy?.toLocaleDateString()}, this claim may
          be cancelled and you may need to resubmit it.
        </p>
      </div>
      <div>
        <ol className="styledCounter leftAlign medium numberCircle">
          <li className="uploadListItem">
            <h3>This claim is for {PetName}</h3>
            <PetSelection
              isReadOnly
              petDetails={petDetails}
              activePolicyNumber={PolicyNumber}
            />
          </li>
          <li className="uploadListItem">
            <h3>Your claim summary</h3>
            <p>{sanitizedAll(ClaimDescription)}</p>
          </li>

          {missingDocsStatuses &&
            missingDocsStatuses.map((key) => {
              const uploadDocDateBy = missingDocs[key];
              const renderStatusHeader = mapUploadFileInfo(
                formContext,
                key,
                uploadDocDateBy,
                PetName,
                isUploading,
                additionalAttachementsByType,
                handleUploadingChange
              );
              return (
                <li
                  className="uploadListItem"
                  key={key}
                >
                  {renderStatusHeader}
                </li>
              );
            })}
        </ol>
      </div>
      <div className="uploadChecklistContainer">
        <h3>Upload Checklist</h3>
        <p>
          Please check the boxes only once you have uploaded all the required information. If you are unable to supply
          this information for any reason, add a comment below. If you don’t upload the requested document, do not tick
          the associated box. Please note this may delay your claim.
        </p>
        {missingDocsStatuses &&
          missingDocsStatuses.map((key, index) => {
            const uploadDocDateBy = missingDocs[key];
            const renderChecklist = mapUploadFileChecklist(formContext, key, uploadDocDateBy);
            return <div key={key || index}>{renderChecklist}</div>;
          })}

        <InputTextarea
          name={UPLOAD_DOCUMENTS_FORM_KEYS.COMMENTS}
          label="Comments"
          placeholder="Add your comments here"
          fieldFormContext={formContext}
          inputCustomCssClass={classNames('uploadRequiredDocumentsComments', {
            borderErrorField: formContext.formState.errors[UPLOAD_DOCUMENTS_FORM_KEYS.COMMENTS]
          })}
          validation={{
            validate: (value) => {
              // Get all checklist fields' values
              // Filter out 'INVOICE', comment is not required for INVOICE missing document
              const checklistValues = missingDocsStatuses
                .filter((status) => status !== 'INVOICE')
                .map((key) => formContext.getValues(`${key}_CHECKLIST`));

              const isInvoiceOnly =
                missingDocsStatuses.length === 1 && missingDocsStatuses[0] === UPLOAD_DOCUMENTS_FORM_KEYS.INVOICE;

              // If missingDoc is INVOICE only or at least one checklist is checked, comment is not required
              if (isInvoiceOnly || checklistValues.some((value) => value === true)) {
                return true;
              }

              // Otherwise, comment is required
              return value && value.trim() !== '' ? true : 'Add your comments';
            }
          }}
        />
      </div>
      <div className="uploadRequiredDocumentsFooter">
        <button
          type="button"
          className="button outline"
          onClick={handleBackToDashboard}
        >
          Back to Dashboard
        </button>
        <button
          type="button"
          className="button outline"
          onClick={handleSaveDraft}
          disabled={isAnyUploading}
        >
          Save Draft
        </button>
        <button
          type="button"
          onClick={() => onFormSubmit()}
          className="button primary"
          disabled={isAnyUploading}
        >
          Submit
        </button>
      </div>
    </div>
  );
};

export default UploadRequiredDocuments;
